// Components/Pages/Media.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaYoutube, FaPlay, FaClock, FaEye, FaTimes } from "react-icons/fa";

const Media = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await fetch('https://api.rigokenya.org/api/media');
      if (!response.ok) {
        throw new Error('Failed to fetch videos');
      }
      const data = await response.json();
      setVideos(data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setError('Failed to load videos. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const formatViews = (views) => {
    if (!views) return '0';
    const num = parseInt(views);
    if (isNaN(num)) return views;
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const openVideo = (video) => {
    setSelectedVideo(video);
    document.body.style.overflow = 'hidden';
  };

  const closeVideo = () => {
    setSelectedVideo(null);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeVideo();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primaryGold mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading videos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 max-w-md mx-auto">
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={fetchVideos}
            className="mt-4 bg-primaryGold text-primaryBlue px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-primaryBlue mb-4">
            Media
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Watch our videos, documentaries, and webinars to see our work in action and hear from the communities we serve.
          </p>
          <div className="w-24 h-1 bg-primaryGold mx-auto mt-4"></div>
        </div>

        {/* YouTube Channel Button */}
        <div className="text-center mb-8">
          <a 
            href="https://www.youtube.com/@risingtogreatness-cbo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <FaYoutube className="text-2xl" />
            Subscribe to Our YouTube Channel
          </a>
        </div>

        {/* Videos Grid */}
        {videos.length === 0 ? (
          <div className="text-center py-12">
            <FaYoutube className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No videos available yet.</p>
            <p className="text-gray-400 text-sm mt-2">Check back soon for updates.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {videos.map((video) => (
              <div 
                key={video.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:transform hover:-translate-y-1 cursor-pointer"
                onClick={() => openVideo(video)}
              >
                {/* Video Thumbnail */}
                <div className="relative group">
                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                    {video.thumbnail ? (
                      <img 
                        src={video.thumbnail} 
                        alt={video.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://via.placeholder.com/400x225/1a365d/ffffff?text=${video.title.replace(/ /g, '+')}`;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-primaryBlue flex items-center justify-center text-white text-6xl">
                        <FaYoutube />
                      </div>
                    )}
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center group-hover:bg-opacity-50 transition-all duration-300">
                      <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 shadow-lg">
                        <FaPlay className="text-white text-2xl ml-1" />
                      </div>
                    </div>
                    {/* Duration Badge - Only show if duration exists */}
                    {video.duration && video.duration !== 'N/A' && (
                      <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    )}
                  </div>
                </div>

                {/* Video Info */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-primaryBlue mb-2 line-clamp-2">
                    {video.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {video.description || 'No description available'}
                  </p>

                  {/* Meta Info - Hide N/A values */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <FaEye />
                      {video.views ? formatViews(video.views) : '0'} views
                    </span>
                    <span>{video.date || ''}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Video Modal */}
        {selectedVideo && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={closeVideo}
          >
            <div 
              className="relative max-w-5xl w-full bg-black rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeVideo}
                className="absolute top-4 right-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all duration-300"
              >
                <FaTimes className="text-xl" />
              </button>

              {/* Video Player */}
              <div className="relative pb-[56.25%] h-0">
                <iframe
                  src={`https://www.youtube.com/embed/${extractYouTubeId(selectedVideo.youtube_url)}?autoplay=1&rel=0`}
                  title={selectedVideo.title}
                  className="absolute top-0 left-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                />
              </div>

              {/* Video Info */}
              <div className="bg-white p-6">
                <h3 className="text-2xl font-bold text-primaryBlue mb-2">
                  {selectedVideo.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {selectedVideo.description || 'No description available'}
                </p>
                <div className="flex items-center gap-6 text-sm text-gray-500">
                  <span className="flex items-center gap-2">
                    <FaEye className="text-primaryGold" />
                    {selectedVideo.views ? formatViews(selectedVideo.views) : '0'} views
                  </span>
                  {selectedVideo.duration && selectedVideo.duration !== 'N/A' && (
                    <span className="flex items-center gap-2">
                      <FaClock className="text-primaryGold" />
                      {selectedVideo.duration}
                    </span>
                  )}
                  <span>{selectedVideo.date || ''}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">Want to collaborate or share your story through our media platforms?</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-block bg-primaryGold text-primaryBlue px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Contact Us
            </Link>
            <Link
              to="/impact-stories"
              className="inline-block bg-primaryBlue text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Read Impact Stories
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Media;